import useSWR from "swr";
import { fetcher } from "@/lib/api";
import KpiTile from "@/components/KpiTile";
import { PageHeader, Section } from "@/components/Heading";
import { formatIndianCurrency, formatIndianGrouping, formatPct } from "@/lib/format";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const TONES = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

export default function HrOverview() {
  const { data } = useSWR("/hr/overview", fetcher);
  const { data: demo } = useSWR("/hr/demographics", fetcher);
  if (!data) return null;

  return (
    <div>
      <PageHeader
        eyebrow="People Intelligence"
        title="The shop floor, as a balance sheet."
        lede="Headcount, attendance, attrition, payroll — every people number that should matter to the CEO."
      />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border">
        <KpiTile testId="kpi-employees" label="Employees" kind="number" value={data.total_employees} accent />
        <KpiTile testId="kpi-attendance" label="Attendance" kind="pct" value={data.attendance_pct} />
        <KpiTile testId="kpi-attrition" label="Attrition" kind="pct" value={data.attrition_rate} />
        <KpiTile testId="kpi-payroll" label="Payroll" value={data.monthly_payroll} />
        <KpiTile testId="kpi-avg-sal" label="Avg salary" value={data.avg_salary} />
        <KpiTile testId="kpi-sales-staff" label="Sales / staff" value={data.sales_per_staff} />
      </div>

      <div className="grid lg:grid-cols-2 gap-10 mt-14">
        <Section eyebrow="Department headcount" title="Where the floor sits">
          <div className="hairline overflow-hidden">
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th>Department</th>
                  <th className="text-right">Headcount</th>
                  <th className="text-right">Attendance</th>
                  <th className="text-right">Avg salary</th>
                </tr>
              </thead>
              <tbody>
                {(data.departments || []).slice(0, 14).map((d) => (
                  <tr key={d.department}>
                    <td className="font-display text-sm">{d.department}</td>
                    <td className="text-right">{d.headcount}</td>
                    <td className={`text-right ${d.attendance_pct < 80 ? "text-warning" : ""}`}>{formatPct(d.attendance_pct)}</td>
                    <td className="text-right">{formatIndianCurrency(d.avg_salary)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        <Section eyebrow="Type breakdown" title="Permanent / Temporary / Intern">
          <div className="hairline p-4">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={demo?.type_split || []} dataKey="value" innerRadius={70} outerRadius={110} stroke="hsl(var(--background))" strokeWidth={3}>
                  {(demo?.type_split || []).map((_, i) => <Cell key={i} fill={TONES[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-around mt-3">
              {(demo?.type_split || []).map((t, i) => (
                <div key={t.name} className="text-center">
                  <div className="w-3 h-3 mx-auto" style={{ background: TONES[i] }} />
                  <div className="label-caps mt-1">{t.name}</div>
                  <div className="font-mono text-sm">{t.value}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
